import React, { SyntheticEvent, useState } from 'react';
import { Controller, Control } from 'react-hook-form';
import {
  Autocomplete,
  TextField,
  Checkbox,
  AutocompleteChangeReason,
  Chip,
} from '@mui/material';

interface InputProps {
  control: Control;
  id: string;
  title: string;
  data: Record<string, string[]>; // Hierarchical data
  selectedParentClasses?: string[];
  disabled?: boolean;
  setFilterValue: (data: Partial<qs.ParsedQs>) => void;
}

export const HierarchicalSelect = ({
  control,
  id,
  title,
  data,
  selectedParentClasses,
  setFilterValue,
  disabled = false,
}: InputProps) => {
  const options = Object.keys(data).reduce<string[]>((acc, parent) => {
    if (data[parent].length === 0) return [...acc];
    else return [...acc, parent, ...data[parent]];
  }, []);
  const parentClass = Object.keys(data)?.map((item) => item);

  const [inputValue, setValue] = useState<string[]>(
    Array.from(
      new Set(
        [...(selectedParentClasses || [])].flatMap((item) =>
          data[item] ? [item, ...data[item]] : [item]
        )
      )
    )
  );

  return (
    <Controller
      name={id}
      control={control}
      render={({ field: { onChange, value } }: any) => {
        const handleSelectionChange = (
          _: SyntheticEvent<Element, Event>,
          selectedOptions: string[],
          reason: AutocompleteChangeReason
        ) => {
          if (reason === 'clear') {
            setValue([]);
            setFilterValue({ brandClass: [] });
            return;
          }

          const updatedSelection = new Set(selectedOptions);
          const previous = new Set(inputValue);
          const current = new Set(selectedOptions);

          const added = [...current]?.filter((x) => !previous.has(x));
          const removed = [...previous]?.filter((x) => !current.has(x));

          added.forEach((item) => {
            if (parentClass.includes(item)) {
              // If we select parent add all of its children classes
              data[item].forEach((child) => updatedSelection.add(child));
              //updatedSelection.add(item);
              setFilterValue({
                brandClass: [...(selectedParentClasses ?? []), item],
              });
            } else {
              // If we select child add chil
              updatedSelection.add(item);
              // If this is the last child selected add parent as well
              parentClass.forEach((parent) => {
                const children = data[parent] || [];
                const allChildrenSelected = children.every((child) =>
                  updatedSelection.has(child)
                );
                if (allChildrenSelected) {
                  updatedSelection.add(parent);
                  setFilterValue({
                    brandClass: [...(selectedParentClasses ?? []), parent],
                  });
                }
              });
            }
          });

          removed.forEach((item) => {
            if (parentClass.includes(item)) {
              // If we remove parent class then remove all childrend classes
              data[item].forEach((child) => updatedSelection.delete(child));
              const newParentClass = selectedParentClasses?.filter(
                (parent) => item !== parent
              );
              setFilterValue({ brandClass: newParentClass });
              // updatedSelection.delete(item);
            } else {
              // if we remove child remove child
              updatedSelection.delete(item);
              // If parent of the child is selected remove it to
              const removedParent = selectedParentClasses?.filter((parent) =>
                data[parent].includes(item)
              );
              if (removedParent) {
                const newParentClass = selectedParentClasses?.filter(
                  (parent) => parent !== removedParent[0]
                );
                setFilterValue({ brandClass: newParentClass });
                updatedSelection.delete(removedParent[0]);
              }
            }
          });

          const final = Array.from(updatedSelection);

          setValue(final);
          onChange(final);
        };
        return (
          <Autocomplete
            disabled={disabled}
            multiple
            id={title}
            options={options}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
            defaultValue={[]}
            value={inputValue}
            onChange={handleSelectionChange}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  display: 'flex',
                  justifyContent: parentClass.includes(option)
                    ? 'center'
                    : 'flex-start',
                }}
              >
                <Checkbox checked={inputValue?.includes(option)} />
                {parentClass.includes(option) ? `${option}-CLASS` : option}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label={title} />}
            renderTags={(_, getTagProps) => {
              return inputValue?.map((option: string, index: number) => (
                <Chip
                  label={
                    parentClass.includes(option) ? `${option}-CLASS` : option
                  }
                  {...getTagProps({ index })}
                />
              ));
            }}
          />
        );
      }}
    />
  );
};
